import {useState} from 'react';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {useUserPref} from '@netx/core-components/hooks/useUserPref';
import {metadataDatasetLookup} from '../api';
import type {AttributeFormValues, AttributesWorking} from '../types';
import {type IAttributeTemplate} from '../schema';
import {getTemplatesEffectedByLookup, isMultiValue} from '../utils';
import useMetadataLookupsAllowed from './useMetadataLookupsAllowed';

export interface UseMetadataLookupsParams {
	attributeTemplates: IAttributeTemplate[];
	attributeIds: number[];
	disableLookups?: boolean;
	onLookup?: (
		values: AttributeFormValues,
		attributeId: number,
		value: string,
	) => void;
}

export const useMetadataLookups = ({
	attributeTemplates,
	attributeIds,
	disableLookups,
	onLookup,
}: UseMetadataLookupsParams) => {
	const [metadataLookupsEnabled] = useUserPref(
		'category.metadataDatasetLookupEnabled',
		{
			asBoolean: true,
		},
	);

	const [attributesWorking, setAttributesWorking] = useState<AttributesWorking>(
		{},
	);
	const [lookupValues, setLookupValues] = useState<AttributeFormValues>({});
	const lookupsAllowed = useMetadataLookupsAllowed(
		attributeTemplates,
		attributeIds,
		disableLookups,
	);

	/** handle attribute metadata lookup */
	const lookup = useConstCallback(
		async (
			attribute: IAttributeTemplate,
			lookupValue: string,
			options: {
				limitToIds?: number[];
			} = {},
		) => {
			if (lookupValue == null || lookupValue === '') {
				setLookupValues({});
				onLookup?.({}, attribute.attributeid, lookupValue);
				return {};
			}

			// Determine which attributes will be updated by lookup
			setAttributesWorking(
				getTemplatesEffectedByLookup(
					attribute,
					attributeTemplates.filter(
						(attr) =>
							!options.limitToIds ||
							options.limitToIds.includes(attr.attributeid),
					),
				),
			);

			// TODO: react-query
			const lookupResults = await metadataDatasetLookup({
				name: attribute.name,
				value: lookupValue,
			});

			setTimeout(() => {
				setAttributesWorking({});
			}, 200);

			const lookupValues = lookupResults.reduce<AttributeFormValues>(
				(values, result) => {
					// ¯\_(ツ)_/¯
					const attributeTemplate = attributeTemplates.find(
						(attr) => attr.name === result.label,
					);
					if (
						!attributeTemplate ||
						attributeTemplate.permission === 'r' ||
						(options.limitToIds &&
							!options.limitToIds.includes(attributeTemplate.attributeid))
					)
						return values;
					return {
						...values,
						[attributeTemplate.attributeid]: isMultiValue(
							attributeTemplate.type,
						)
							? [result.value]
							: result.value,
					};
				},
				{},
			);

			setLookupValues(lookupValues);

			// For each result, update the attribute values if possible
			onLookup?.(lookupValues, attribute.attributeid, lookupValue);

			return lookupValues;
		},
	);

	return {
		attributesWorking,
		lookupsAllowed,
		lookupValues,
		lookup,
		canLookup:
			metadataLookupsEnabled &&
			(!!lookupsAllowed.keys.length || !!lookupsAllowed.targets.length),
	};
};

export default useMetadataLookups;
